<template>
  <div
    v-if="author"
    class="flex flex-col"
    :class="{
      'w-full bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-800 shadow-xl rounded-2xl p-5 transition-all duration-300 hover:shadow-2xl':
        showDetail,
      'w-full lg:w-1/3': !showDetail,
    }"
  >
    <div
      class="flex justify-between"
      :class="{
        'bg-gray-100/50 dark:bg-[#12242b]/50 border border-gray-300 dark:border-gray-700 rounded-xl mr-2 p-3':
          !showDetail,
      }"
    >
      <div class="flex items-start max-w-full w-full">
        <profile-image
          v-if="author.name"
          class="relative shrink-0 mr-4"
          :size="48"
          :img-url="avatarUrlFromString(author.name)"
        />
        <div class="w-full">
          <div class="w-full flex justify-between items-start">
            <a
              :href="`${webBaseUrl || ''}/research-compass/author/${sqidEncode(
                author.authorId,
              )}`"
              class="group"
              target="_blank"
            >
              <div class="flex items-center flex-wrap gap-2">
                <h6
                  class="text-lg font-headline font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors flex items-center gap-2"
                  :title="author.name"
                >
                  <span>{{ author.name }}</span>
                  <i
                    class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                  ></i>
                </h6>
                <span
                  v-if="
                    showDetail &&
                    includeResearchers &&
                    author.normalizedFitScore > 80
                  "
                  class="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/50 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider inline-flex items-center"
                >
                  <i class="fas fa-star mr-1"></i>
                  Recommended
                </span>
              </div>
            </a>
            <div class="flex items-start gap-3">
              <div v-if="includeResearchers" class="flex justify-end">
                <template v-if="showDetail">
                  <button
                    v-if="!isMeetingRequested"
                    class="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold rounded-lg transition-colors shadow-sm whitespace-nowrap mt-0.5"
                    @click="requestMeeting"
                  >
                    Request Meeting
                  </button>
                  <button
                    v-else
                    class="pointer-events-none bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/50 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap mt-0.5"
                  >
                    <i class="fas fa-check mr-1"></i>
                    Meeting Requested
                  </button>
                </template>
                <template v-if="!showDetail">
                  <research-compass-request-meeting-dropdown
                    class="mt-0.5"
                    @request-meeting="requestMeeting"
                  />
                </template>
              </div>
              <div class="flex items-start gap-1 mt-1">
                <feedback-modal
                  sentiment="POSITIVE"
                  item-type="EXPERT"
                  :item-id="question.hashedId"
                  :ancillary-information="author.authorId"
                  :ancillary-information-label="author.name"
                  :research-compass-hashed-id="$route.params.researchCompassId"
                />
                <feedback-modal
                  sentiment="NEGATIVE"
                  item-type="EXPERT"
                  :item-id="question.hashedId"
                  :ancillary-information="author.authorId"
                  :ancillary-information-label="author.name"
                  :research-compass-hashed-id="$route.params.researchCompassId"
                />
              </div>
            </div>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <p
              v-for="(institution, index) in deduplicatedAffiliations"
              :key="`institution${index}`"
              :title="institution"
              class="text-ellipsis whitespace-nowrap font-semibold"
            >
              <i class="fas fa-university mr-1.5 opacity-60"></i>
              {{ institution }}
            </p>
          </div>

          <div
            v-if="showDetail && author.hasCorpusMetrics"
            class="py-2 flex flex-wrap"
          >
            <div class="w-full">
              <template v-if="author.suitability?.goodFitReason">
                <div
                  class="bg-emerald-500/5 dark:bg-emerald-400/5 border-l-4 border-emerald-500/50 dark:border-emerald-400/30 rounded-r-lg px-5 py-4 mb-4 text-sm text-gray-700 dark:text-gray-300 italic shadow-sm"
                >
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1 not-italic"
                  >
                    Why this expert?
                  </div>
                  {{ author.suitability.goodFitReason }}
                </div>
              </template>
            </div>
          </div>
          <div
            v-else-if="showDetail && !author.hasCorpusMetrics"
            class="py-4 text-center"
          >
            <div class="w-full text-gray-500 dark:text-gray-400 italic text-sm">
              <i class="fas fa-circle-notch fa-spin mr-2"></i> Loading
              researcher metadata...
            </div>
          </div>
          <div
            v-if="showDetail && author.hasCorpusMetrics"
            class="flex flex-wrap border-t border-gray-200 dark:border-gray-800 pt-4 mt-2"
          >
            <div
              class="py-2 mb-2 w-full md:w-1/2 lg:w-1/3 flex flex-col gap-4 pr-5"
            >
              <div class="flex flex-col text-xs">
                <div
                  class="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-1"
                >
                  Citations
                </div>
                <div
                  class="font-mono font-bold text-gray-900 dark:text-white flex items-center gap-2"
                >
                  <span
                    class="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700"
                    >{{ author.citationCount }}</span
                  >
                  <span
                    v-if="author.influentialCitationCount > 0"
                    class="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-0.5 rounded flex items-center gap-1 border border-yellow-200 dark:border-yellow-700/50"
                  >
                    <i class="fas fa-star text-[10px]"></i>
                    <span
                      >{{ author.influentialCitationCount }} influential</span
                    >
                  </span>
                </div>
              </div>
              <div class="flex flex-col text-xs">
                <div
                  class="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-1"
                >
                  Active
                </div>
                <div
                  class="font-mono font-bold text-gray-900 dark:text-white flex items-center"
                >
                  <span
                    class="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded mr-2"
                    >{{ author.yearsActive }} yrs</span
                  >
                  <span class="text-gray-400 dark:text-gray-500 font-normal"
                    >{{ author.firstPublishedYear }}-{{
                      author.latestPublishedYear
                    }}</span
                  >
                </div>
              </div>
              <div class="flex flex-col text-xs">
                <div
                  class="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-1"
                >
                  Total Papers
                </div>
                <div
                  class="font-mono font-bold text-gray-900 dark:text-white flex items-center"
                >
                  <span
                    class="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded mr-2"
                    >{{ author.paperCount }}</span
                  >
                  <span class="text-gray-400 dark:text-gray-500 font-normal"
                    >Recent: {{ author.paperCountLast_5Years }}</span
                  >
                </div>
              </div>
              <div class="flex flex-col text-xs">
                <div
                  class="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-1"
                >
                  Q1 Journal Percentage
                </div>
                <div
                  class="font-mono font-bold text-emerald-700 dark:text-emerald-400 flex items-center"
                >
                  <span
                    class="bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 rounded"
                  >
                    {{
                      Math.floor(
                        (
                          (author.journalQuartileCounts?.Q1 || 0) /
                            ((author.journalQuartileCounts?.Q1 || 0) +
                              (author.journalQuartileCounts?.Q2 || 0) +
                              (author.journalQuartileCounts?.Q3 || 0) +
                              (author.journalQuartileCounts?.Q4 || 0)) || 0
                        ).toFixed(2) * 100,
                      )
                    }}%
                  </span>
                </div>
              </div>
            </div>
            <div
              class="py-2 w-full md:w-1/2 lg:w-2/3 border-l border-gray-200 dark:border-gray-800 pl-5"
            >
              <template v-if="!isConcept">
                <div
                  v-if="author.suitability.mostRelevantPaperTitles?.length > 0"
                  class=""
                >
                  <div
                    v-if="author.suitability.mostRelevantPapers?.length > 0"
                    class="text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold tracking-widest block mb-3"
                  >
                    Most Relevant Publications
                  </div>
                  <div
                    v-else-if="
                      author.suitability.mostRelevantPaperTitles?.length > 0
                    "
                    class="text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold tracking-widest block mb-3"
                  >
                    Most Relevant Publications
                  </div>
                  <template
                    v-if="author.suitability.mostRelevantPapers?.length > 0"
                  >
                    <div
                      v-for="paper in author.suitability.mostRelevantPapers"
                      :key="paper.title"
                      class="mb-3 flex items-start group"
                    >
                      <i
                        class="fas fa-file-lines text-emerald-600/50 dark:text-emerald-400/50 mt-1 mr-2 text-[10px]"
                      ></i>
                      <a
                        v-if="paper.id"
                        :href="`${
                          webBaseUrl || ''
                        }/research-compass/paper/${sqidEncode(paper.id)}`"
                        target="_blank"
                        class="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline transition-all flex items-center gap-1.5"
                      >
                        {{ paper.title }}
                        <i
                          class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                        ></i>
                      </a>
                      <a
                        v-else-if="
                          paper.semanticScholarId &&
                          (paper.semanticScholarId.startsWith('CorpusID:') ||
                            /^\d+$/.test(paper.semanticScholarId))
                        "
                        :href="`${
                          webBaseUrl || ''
                        }/research-compass/paper/${sqidEncode(
                          parseInt(
                            paper.semanticScholarId.replace('CorpusID:', ''),
                          ),
                        )}`"
                        target="_blank"
                        class="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline transition-all flex items-center gap-1.5"
                      >
                        {{ paper.title }}
                        <i
                          class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                        ></i>
                      </a>
                      <span
                        v-else
                        class="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5"
                      >
                        {{ paper.title }}
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    <div
                      v-for="paperTitle in author.suitability
                        .mostRelevantPaperTitles"
                      :key="paperTitle"
                      class="mb-3 flex items-start group"
                    >
                      <i
                        class="fas fa-file-lines text-emerald-600/50 dark:text-emerald-400/50 mt-1 mr-2 text-[10px]"
                      ></i>
                      <span
                        class="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5"
                      >
                        {{ paperTitle }}
                      </span>
                    </div>
                  </template>
                </div>
              </template>
              <template v-else>
                <div class="flex justify-between">
                  <div>
                    <strong class="text-sm">Concept Relevance Score</strong>
                  </div>
                  <div>
                    <strong class="text-sm">{{
                      author.suitability.conceptScore / 100
                    }}</strong>
                  </div>
                </div>
                <div v-if="author.suitability?.conceptScoreReason" class="mt-2">
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Concept Score Reason
                  </div>
                  <div class="text-sm text-gray-700 dark:text-gray-300 italic">
                    {{ author.suitability.conceptScoreReason }}
                  </div>
                </div>
                <div v-if="author.suitability?.conceptPaperTitles" class="mt-4">
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Concept Paper Titles
                  </div>
                  <ul
                    class="text-sm list-disc ml-5 text-gray-700 dark:text-gray-300"
                  >
                    <li
                      v-for="title in author.suitability?.conceptPaperTitles"
                      :key="title"
                    >
                      {{ title }}
                    </li>
                  </ul>
                </div>
                <div
                  v-if="author.suitability?.conceptPaperRelevance"
                  class="mt-4"
                >
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Concept Paper Relevance
                  </div>
                  <div class="text-sm text-gray-700 dark:text-gray-300 italic">
                    {{ author.suitability.conceptPaperRelevance }}
                  </div>
                </div>
              </template>

              <div v-if="aggregatedFunders.length > 0" class="mt-6">
                <div
                  class="text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold tracking-widest mb-3"
                >
                  Top Funders
                </div>

                <div class="flex flex-col">
                  <div
                    v-for="funder in aggregatedFunders"
                    :key="funder.id"
                    class="mb-2 flex items-start group"
                  >
                    <i
                      class="fas fa-building-columns text-emerald-600/50 dark:text-emerald-400/50 mt-1 mr-2 text-[10px]"
                    ></i>
                    <div class="flex flex-col">
                      <span
                        class="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight"
                      >
                        {{ funder.name }}
                      </span>
                      <span
                        class="text-[10px] text-gray-500 font-bold uppercase mt-0.5"
                      >
                        {{ funder.count }}
                        {{ funder.count === 1 ? "Grant" : "Grants" }}
                      </span>
                    </div>
                  </div>
                </div>

                <a
                  v-if="totalFunderCount > 5"
                  :href="`${
                    webBaseUrl || ''
                  }/research-compass/author/${sqidEncode(
                    author.authorId || author.author_id,
                  )}/`"
                  target="_blank"
                  class="mt-3 block text-[10px] font-bold uppercase tracking-wider text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
                >
                  And {{ totalFunderCount - 5 }} More
                  <i class="fas fa-arrow-right ml-0.5"></i>
                </a>
              </div>
              <div v-else class="mt-6">
                <div
                  class="text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold tracking-widest mb-1.5"
                >
                  Top Funders
                </div>
                <div class="text-xs text-gray-400 dark:text-gray-500 italic">
                  Funding data is currently unavailable for this researcher.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <regular-modal ref="requestMeetingModal" :showing="false">
      <template #header>
        <h4 class="text-xl font-headline">Request Meeting</h4>
      </template>
      <template #body>
        <div class="w-full max-h-[calc(100vh-250px)] overflow-auto px-6 py-4">
          <div class="relative w-full mb-3">
            <p>
              Please provide additional information and we'll help set up a
              meeting with {{ author.name }}.
            </p>
          </div>
          <div class="flex">
            <div class="relative w-full mb-3 mr-3">
              <label class="form-label"> First Name </label>
              <input
                ref="firstName"
                v-model="contactForm.firstName"
                type="text"
                class="form-text-input"
              />
            </div>
            <div class="relative w-full mb-3">
              <label class="form-label"> Last Name </label>
              <input
                ref="lastName"
                v-model="contactForm.lastName"
                type="text"
                class="form-text-input"
              />
            </div>
          </div>
          <div class="relative w-full mb-3">
            <label class="form-label"> Email Address </label>
            <input
              ref="emailAddress"
              v-model="contactForm.emailAddress"
              type="text"
              class="form-text-input"
            />
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex w-full justify-between items-center">
          <div>
            <div v-if="contactForm.errorMessage">
              <p class="text-sm text-red-500">
                {{ contactForm.errorMessage }}
              </p>
            </div>
          </div>
          <div>
            <button
              class="modal-nonprimary-button"
              type="submit"
              @click="toggleRequestMeetingModal()"
            >
              <span>Cancel</span>
            </button>
            <button
              class="modal-primary-button"
              type="submit"
              @click="submitPublicRequestMeetingForm()"
            >
              <span>Submit</span>
            </button>
          </div>
        </div>
      </template>
    </regular-modal>
    <regular-modal ref="requestMeetingConfirmationModal" :showing="false">
      <template #header>
        <h4 class="text-xl font-headline">Meeting Requested</h4>
      </template>
      <template #body>
        <div class="w-full max-h-[calc(100vh-250px)] overflow-auto px-6 py-4">
          <div class="relative w-full mb-3">
            <p>
              A request has been sent to the NotedSource team. Please expect to
              hear from us via email about a meeting with {{ author.name }}.
            </p>
          </div>
        </div>
      </template>
      <template #footer>
        <button
          class="modal-primary-button"
          type="submit"
          @click="toggleRequestMeetingConfirmationModal()"
        >
          <span>Okay</span>
        </button>
      </template>
    </regular-modal>
  </div>
</template>

<script>
import axios from "axios";
import * as utils from "../../utils";
import ProfileImage from "../ProfileImage.vue";
import RegularModal from "../Modals/Regular.vue";
import ResearchCompassRequestMeetingDropdown from "./ResearchCompassRequestMeetingDropdown.vue";
import FeedbackModal from "./FeedbackModal.vue";

const { avatarUrlFromString, keysToSnake, sqidEncode } = utils;

export default {
  components: {
    ProfileImage,
    RegularModal,
    ResearchCompassRequestMeetingDropdown,
    FeedbackModal,
  },
  props: {
    question: Object,
    author: Object,
    isConcept: Boolean,
    showDetail: Boolean,
    includeResearchers: Boolean,
  },
  data() {
    return {
      webBaseUrl:
        import.meta.env.VITE_WEB_BASE_URL || import.meta.env.VITE_BASE_URL,
      avatarUrlFromString,
      journalsExpanded: false,
      isMeetingRequested: false,
      contactForm: {
        errorMessage: null,
        firstName: null,
        lastName: null,
        emailAddress: null,
      },
      axios,
    };
  },
  computed: {
    totalFunderCount() {
      if (!this.author.funders || this.author.funders.length === 0) return 0;

      const funderMap = {};
      this.author.funders.forEach((funder) => {
        const id = funder.funder;
        if (!funderMap[id]) funderMap[id] = true;
      });
      return Object.keys(funderMap).length;
    },
    aggregatedFunders() {
      if (!this.author.funders || this.author.funders.length === 0) return [];

      const funderMap = {};
      this.author.funders.forEach((funder) => {
        const name =
          funder.funderDisplayName || funder.funder_display_name || "Unknown";
        const id = funder.funder;
        if (!funderMap[id]) funderMap[id] = { id, name, count: 0 };
        funderMap[id].count++;
      });
      return Object.values(funderMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    },
    deduplicatedAffiliations() {
      const institutions =
        this.author.structuredAffiliations?.map((a) => a.institution) || [];
      const normalizedMap = new Map();
      institutions.forEach((inst) => {
        if (!inst) return;
        // Normalize single quotes, double quotes, and backticks to a standard single quote
        const normalized = inst.replace(/['"`‘’“”]/g, "'").trim();
        if (!normalizedMap.has(normalized)) {
          normalizedMap.set(normalized, inst);
        }
      });
      return Array.from(normalizedMap.values());
    },
    fiveYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 5);
      return date.getFullYear();
    },
  },
  methods: {
    sqidEncode(id) {
      return sqidEncode(id);
    },
    requestMeeting() {
      if (this.$store.getters.person) {
        this.$api.axios
          .post(
            `/collaboration-space/${this.$route.params.collaborationSpaceId}/research-compass/${this.$route.params.researchCompassId}/request-meeting/`,
            keysToSnake({
              researcherName: this.author.name,
              researcherId: this.author.authorId,
              questionId: this.question ? this.question.hashedId : null,
            }),
          )
          .then(() => {
            this.isMeetingRequested = true;
            this.toggleRequestMeetingConfirmationModal();
          });
      } else {
        this.toggleRequestMeetingModal();
      }
    },
    submitPublicRequestMeetingForm() {
      if (
        !this.contactForm.firstName ||
        !this.contactForm.lastName ||
        !this.contactForm.emailAddress
      ) {
        this.contactForm.errorMessage = "All fields are required.";
      } else {
        this.contactForm.errorMessage = null;
        axios
          .post(
            `${import.meta.env.VITE_API_BASE_URL}/research-compass/${
              this.$route.params.researchCompassId
            }/request-meeting/?view_key=${this.$route.params.viewKey}`,
            keysToSnake({
              firstName: this.contactForm.firstName,
              lastName: this.contactForm.lastName,
              emailAddress: this.contactForm.emailAddress,
              researcherName: this.author.name,
              researcherId: this.author.authorId,
              questionId: this.question ? this.question.hashedId : null,
            }),
          )
          .then(() => {
            this.isMeetingRequested = true;
            this.toggleRequestMeetingModal();
            this.toggleRequestMeetingConfirmationModal();
          });
      }
    },
    toggleRequestMeetingModal() {
      this.$refs.requestMeetingModal.toggleModal();
    },
    toggleRequestMeetingConfirmationModal() {
      this.$refs.requestMeetingConfirmationModal.toggleModal();
    },
  },
};
</script>
